import React, { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";

import RefreshIcon from "components/icons/components/RefreshIcon";
import type { ColumnMapping } from "../types";

interface ColumnMappingModalProps {
  open: boolean;
  columns: string[];
  sampleData: Record<string, string>[];
  onConfirm: (mappings: ColumnMapping[]) => void;
  onCancel: () => void;
}

const ColumnMappingModal: React.FC<ColumnMappingModalProps> = ({ open, columns, sampleData, onConfirm, onCancel }) => {
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [sampleIndices, setSampleIndices] = useState<Record<string, number>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Filter out empty columns: columns with empty header OR all empty values in sample data
  const filteredColumns = columns.filter((col) => {
    if (!col.trim()) return false;
    if (sampleData.length > 0) {
      const allEmpty = sampleData.every((row) => !row[col]?.trim());
      if (allEmpty) return false;
    }
    return true;
  });

  useEffect(() => {
    const initialMappings: Record<string, string> = {};
    const initialIndices: Record<string, number> = {};
    filteredColumns.forEach((col) => {
      initialMappings[col] = col;
      initialIndices[col] = 0;
    });
    setMappings(initialMappings);
    setSampleIndices(initialIndices);
    setErrors({});
  }, [columns]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVariableNameChange = useCallback((originalCol: string, value: string) => {
    setMappings((prev) => ({ ...prev, [originalCol]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[originalCol];
      return next;
    });
  }, []);

  const cycleSample = useCallback(
    (col: string) => {
      if (sampleData.length === 0) return;
      setSampleIndices((prev) => ({
        ...prev,
        [col]: (prev[col] + 1) % sampleData.length,
      }));
    },
    [sampleData]
  );

  const handleConfirm = useCallback(() => {
    const newErrors: Record<string, string> = {};
    const usedNames = new Set<string>();

    filteredColumns.forEach((col) => {
      const name = mappings[col]?.trim();
      if (!name) {
        newErrors[col] = "Variable name is required";
      } else if (usedNames.has(name.toLowerCase())) {
        newErrors[col] = "Variable name must be unique";
      } else {
        usedNames.add(name.toLowerCase());
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const result: ColumnMapping[] = filteredColumns.map((col) => ({
      originalName: col,
      variableName: mappings[col].trim(),
    }));
    onConfirm(result);
  }, [columns, mappings, onConfirm]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const modal = (
    <div style={overlayStyle}>
      {/* Backdrop */}
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)" }} onClick={onCancel} />

      {/* Modal */}
      <div style={modalStyle}>
        {/* Close button */}
        <div style={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
          <div onClick={onCancel} style={{ cursor: "pointer" }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M6 6L18 18M18 6L6 18"
                stroke="#0C172C"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h2 style={titleStyle}>Confirm Column Mapping</h2>

        {/* Description */}
        <p style={descriptionStyle}>
          Your uploaded file has the columns listed below. Edit the{" "}
          <span style={{ fontWeight: 700 }}>Variable Name</span> to match how each field appears on your flyer, then
          click Confirm.
        </p>

        {/* Table */}
        <div style={{ width: "100%", marginTop: 16, border: "1px solid #E2E2FC", borderRadius: 8, overflow: "hidden" }}>
          {/* Table Header */}
          <div style={tableHeaderRowStyle}>
            <div style={{ ...tableHeaderCellStyle, flex: 1 }}>Your Column</div>
            <div style={{ ...tableHeaderCellStyle, flex: 1 }}>
              Variable Name <span style={{ color: "#E00404" }}>*</span>
            </div>
            <div style={{ ...tableHeaderCellStyle, flex: 1 }}>Sample</div>
          </div>

          {/* Table Body */}
          <div style={tableBodyStyle}>
            {filteredColumns.map((col) => {
              const sampleIdx = sampleIndices[col] || 0;
              const sampleValue = sampleData.length > 0 ? sampleData[sampleIdx]?.[col] || "" : "";

              return (
                <div key={col} style={tableRowStyle}>
                  {/* Your Column */}
                  <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center" }}>
                    <span style={columnValueStyle} title={col}>
                      {col}
                    </span>
                  </div>

                  {/* Variable Name */}
                  <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" as const }}>
                    <input
                      type="text"
                      value={mappings[col] || ""}
                      onChange={(e) => handleVariableNameChange(col, e.target.value)}
                      className="cm-variable-input"
                      style={{
                        ...inputStyle,
                        border: errors[col] ? "1px solid #E00404" : "1px solid transparent",
                        background: errors[col] ? "#FFF5F5" : "#FFF",
                      }}
                    />
                    {errors[col] && (
                      <span style={{ color: "#E00404", fontSize: 11, marginTop: 2, display: "block" }}>
                        {errors[col]}
                      </span>
                    )}
                  </div>

                  {/* Sample */}
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <span style={sampleValueStyle} title={sampleValue}>
                      {sampleValue || "empty"}
                    </span>
                    {sampleData.length > 1 && (
                      <button onClick={() => cycleSample(col)} style={refreshButtonStyle} title="Show next sample">
                        <RefreshIcon />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer buttons */}
        <div style={footerStyle}>
          <button onClick={onCancel} style={cancelButtonStyle}>
            Cancel
          </button>
          <button onClick={handleConfirm} style={confirmButtonStyle}>
            Confirm
          </button>
        </div>
      </div>
      <style>{`.cm-variable-input:hover:not(:focus) { border-color: #D2D2FD !important; } .cm-variable-input:focus { border-color: #4F5B94 !important; }`}</style>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

// ─── Styles ────────────────────────────────────────────────────

const FONT: React.CSSProperties = { fontFamily: "Inter, sans-serif" };

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 9999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  ...FONT,
};

const modalStyle: React.CSSProperties = {
  position: "relative",
  display: "inline-flex",
  padding: "24px 24px 32px 24px",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: 0,
  borderRadius: 8,
  border: "1px solid #E2E2FC",
  background: "#FFF",
  boxShadow: "0 2px 16px 0 rgba(37, 59, 97, 0.10)",
  width: 720,
  maxHeight: "85vh",
  overflow: "hidden",
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  color: "#222336",
  textAlign: "center",
  fontSize: 18,
  fontWeight: 700,
  lineHeight: "18px",
  ...FONT,
};

const descriptionStyle: React.CSSProperties = {
  margin: "16px 0 0 0",
  color: "#2E3860",
  textAlign: "center",
  fontSize: 12,
  fontWeight: 400,
  lineHeight: "16px",
  ...FONT,
};

const tableHeaderRowStyle: React.CSSProperties = {
  display: "flex",
  gap: 12,
  width: "100%",
  borderBottom: "1px solid #E2E2FC",
  padding: "10px 12px 8px 12px",
};

const tableHeaderCellStyle: React.CSSProperties = {
  color: "#2E3860",
  fontSize: 11,
  fontWeight: 700,
  lineHeight: "16px",
  textTransform: "uppercase" as const,
  letterSpacing: "0.44px",
  ...FONT,
};

const tableBodyStyle: React.CSSProperties = {
  width: "100%",
  maxHeight: 320,
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
};

const tableRowStyle: React.CSSProperties = {
  display: "flex",
  gap: 12,
  alignItems: "center",
  width: "100%",
  padding: "10px 12px",
  borderBottom: "1px solid #E2E2FC",
};

const columnValueStyle: React.CSSProperties = {
  color: "#A0A0B8",
  fontSize: 13,
  fontWeight: 400,
  lineHeight: "18px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap" as const,
  ...FONT,
};

const inputStyle: React.CSSProperties = {
  display: "flex",
  height: 40,
  minHeight: 40,
  maxHeight: 40,
  padding: "4px 8px",
  alignItems: "center",
  gap: 8,
  alignSelf: "stretch",
  borderRadius: 6,
  border: "1px solid transparent",
  background: "#FFF",
  outline: "none",
  width: "100%",
  boxSizing: "border-box" as const,
  color: "#222336",
  fontSize: 13,
  fontWeight: 400,
  lineHeight: "18px",
  transition: "border-color 0.15s",
  ...FONT,
};

const sampleValueStyle: React.CSSProperties = {
  overflow: "hidden",
  color: "rgba(34, 35, 54, 0.50)",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap" as const,
  fontSize: 13,
  fontWeight: 400,
  lineHeight: "18px",
  flex: 1,
  minWidth: 0,
  ...FONT,
};

const refreshButtonStyle: React.CSSProperties = {
  flexShrink: 0,
  width: 28,
  height: 28,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 4,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  padding: 0,
};

const footerStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  width: "100%",
  marginTop: 16,
};

const cancelButtonStyle: React.CSSProperties = {
  display: "flex",
  minHeight: 40,
  maxHeight: 40,
  padding: "0px 24px",
  justifyContent: "center",
  alignItems: "center",
  gap: 8,
  textTransform: "none" as const,
  borderRadius: 16,
  border: "1px solid #273754",
  fontSize: 14,
  color: "#00112B",
  fontWeight: 700,
  background: "#FFF",
  cursor: "pointer",
  ...FONT,
};

const confirmButtonStyle: React.CSSProperties = {
  display: "flex",
  minHeight: 40,
  maxHeight: 40,
  padding: "0 24px",
  justifyContent: "center",
  alignItems: "center",
  gap: 8,
  textTransform: "none" as const,
  borderRadius: 16,
  border: "none",
  fontSize: 14,
  color: "#FFFFFF",
  fontWeight: 700,
  background: "#EE4360",
  cursor: "pointer",
  ...FONT,
};

export default ColumnMappingModal;
